import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from '@tanstack/react-query';
import dayjs from "dayjs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Combobox,
  Stack,
} from "@altimateai/lego";
import { DownloadTable } from "@/components/downloadTable";
import { EmptyScreen } from "../../misc";
import { Paginate } from "@/components/paginate";
import { downloadAutoTuneHistoryCSV, getWarehouseAutoSavingsAuditEventsV2 } from "@/helpers/apis";
import { VerticalTimeline, VerticalTimelineElement } from "react-vertical-timeline-component";
import "react-vertical-timeline-component/style.min.css";
import TimelineEventIconWithDate from "./TimelineIcon";
import TimeFrameSelectorDropdown from "./TimelineDropdown";
import { timeline_contentStyle, iconStyle } from "../../constants";
import styles from "../../styles.module.scss";
import { Spinner } from "@altimateai/lego";

const GET_AUTO_TUNE_HISTORY = "GET_AUTO_TUNE_HISTORY";

interface AutoTuneHistoryProps {
  warehouseRK: string;
}

const AutoTuneHistory = ({ warehouseRK }: AutoTuneHistoryProps) => {
  const timeframes = [
    { label: "Day", value: 1 },
    { label: "Week", value: 7 },
    { label: "Last 30 Days", value: 30 },
  ];

  const eventTypeOptions = [
    { label: "Warehouse Scaling", value: "warehouse_scaling" },
    { label: "Warehouse Suspension", value: "warehouse_suspension" },
    { label: "Warehouse Size Changes", value: "warehouse_resized" },
    { label: "Warehouse Backoff", value: "warehouse_resize_backoff" },
    { label: "Config changes", value: "config_changes" },
  ];

  const { instance_id } = useParams();
  const [eventTypeValues, setEventTypeValues] = useState<string[]>([]);
  const [period, setPeriod] = useState(30);
  const [page, setPage] = useState(1);

  const { data: autoTuneHistory, isLoading: autoTuneHistoryLoading } = useQuery({
    queryKey: [GET_AUTO_TUNE_HISTORY, instance_id, warehouseRK, page, period, eventTypeValues],
    queryFn: () =>
      getWarehouseAutoSavingsAuditEventsV2(instance_id || "", warehouseRK, {
        page: page,
        size: 5,
        period,
        event_types: eventTypeValues.length === 0 ? undefined : eventTypeValues,
      }),
  });

  const handleDownload = async () => {
    await downloadAutoTuneHistoryCSV(instance_id || "", warehouseRK, {
      period: period.toString(10),
      event_types: eventTypeValues.length === 0 ? undefined : eventTypeValues,
    });
  };

  return (
    <div className={styles.auto_tune_history}>
      <Stack align={"center"} justify={"between"}>
        <div className={styles.title}>Auto tune history</div>
        <Stack align={"center"} gap={"sm"}>
          <Combobox
            options={eventTypeOptions}
            onChange={setEventTypeValues}
            multiSelect
            value={eventTypeValues}
            showApplyButton
            header={"Event Types"}
            buttonProps={{
              className: "!al-w-80",
              "aria-label": "Event Types",
            }}
            placeholder={"All Events"}
          />
          <DownloadTable
            handleDownload={handleDownload}
            buttonProps={{
              size: "sm",
            }}
          />
          <div className="text-muted">
            {dayjs().subtract(period, "days").format("DD MMM 'YY")}
            {" - "}
            {dayjs().format("DD MMM 'YY")}
          </div>
          <div className={styles.timeframe_inside_graph}>
            <TimeFrameSelectorDropdown options={timeframes} period={period} setPeriod={setPeriod} />
          </div>
        </Stack>
      </Stack>
      {autoTuneHistoryLoading && (
        <Stack justify={"center"} align={"center"} className={"al-w-full al-h-80"}>
          <Spinner />
          Loading events...
        </Stack>
      )}
      {autoTuneHistory &&
        (autoTuneHistory.total === 0 ? (
          <div className={"al-mt-4"}>
            <EmptyScreen
              title="No Audit Events yet"
              subTitle="No Decisions found for the selected period. You will see the data once decisions are made."
            />
          </div>
        ) : (
          <>
            <VerticalTimeline
              className={styles.vertical_timeline}
              layout={"1-column-left"}
              lineColor={"#E7E8EA"}
            >
              {autoTuneHistory?.items.map((event: unknown) => {
                const e = event as {
                  rk: string;
                  event_type: string;
                  change_timestamp: string;
                  reason?: string;
                  event_message: string;
                };
                return (
                  <VerticalTimelineElement
                    key={e.rk}
                    className={styles.vertical_timeline_element}
                    contentStyle={timeline_contentStyle}
                    contentArrowStyle={{
                      display: "none",
                    }}
                    iconStyle={iconStyle}
                    icon={TimelineEventIconWithDate(e.event_type, e.change_timestamp)}
                  >
                    {e.reason && e.reason !== "" ? (
                      <Accordion type="single" collapsible className="al-w-full">
                        <AccordionItem className={"!al-border-0"} value="item-1">
                          <AccordionTrigger
                            className={
                              "!al-p-0 al-decoration-0 hover:al-decoration-0 hover:al-no-underline al-font-normal"
                            }
                          >
                            {e.event_message}
                          </AccordionTrigger>
                          <AccordionContent className={"al-mt-3 al-font-light"}>
                            {e.reason}
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    ) : (
                      <span>{e.event_message}</span>
                    )}
                  </VerticalTimelineElement>
                );
              })}
            </VerticalTimeline>
            <Paginate
              itemCount={autoTuneHistory.total}
              page={page - 1}
              pageSize={autoTuneHistory.size}
              numPages={autoTuneHistory.pages}
              onPageClick={(page: number) => setPage(page + 1)}
              onPageSizeChange={() => {}}
            />
          </>
        ))}
    </div>
  );
};

export default AutoTuneHistory;
